import React, { useState } from "react";
import { useSubmitCmpBetaFeedbackMutation } from "../../../graphql/types";
import { commonStyles } from "../../common/commonStyles";

const Help: React.FC = () => {
  const [messageText, setMessageText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [wasSuccessful, setWasSuccessful] = useState<boolean | null>(null);

  const [submitCmpBetaFeedbackMutation] = useSubmitCmpBetaFeedbackMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!messageText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitMessage("");
    setWasSuccessful(null);

    try {
      const compiledMessage = `Help Inquiry\n\nMessage:\n${messageText.trim()}`;

      const result = await submitCmpBetaFeedbackMutation({
        variables: {
          feedback: compiledMessage,
          urlPath: window.location.pathname,
        },
      });

      if (result.data?.submitCMPBetaFeedback?.success) {
        setWasSuccessful(true);
        setSubmitMessage("Help request received. We'll reach out ASAP.");
        setMessageText("");
      } else {
        setWasSuccessful(false);
        setSubmitMessage(
          result.data?.submitCMPBetaFeedback?.message ||
            "Failed to send your inquiry. Please try again."
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error submitting help inquiry:", error);
      setWasSuccessful(false);
      setSubmitMessage("Failed to send your inquiry. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center h-full min-h-[60vh]">
      <div className="max-w-xl w-full p-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Need help?
        </h1>
        <p className="text-gray-700 mb-6">
          Tell us what you need help with and we’ll get back to you as soon as
          possible at the email on your account.
        </p>

        {submitMessage && (
          <div
            className={`mb-4 p-3 rounded-md text-sm ${
              wasSuccessful === true
                ? "bg-green-100 text-green-800 border border-green-200"
                : "bg-red-100 text-red-800 border border-red-200"
            }`}
            role="status"
            aria-live="polite"
          >
            {submitMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} aria-label="Help inquiry form">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label
                htmlFor="messageText"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                How can we help?
              </label>
              <textarea
                id="messageText"
                name="messageText"
                value={messageText}
                onChange={({ currentTarget }) =>
                  setMessageText(currentTarget.value)
                }
                placeholder="Describe the issue or question..."
                className="w-full h-36 p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-cpaGold focus:border-transparent"
                aria-label="Describe your help request"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="flex items-center justify-end">
              <button
                type="submit"
                disabled={!messageText.trim() || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                style={{ backgroundColor: commonStyles.cpaGold }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor =
                      commonStyles.cpaGoldDark;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor =
                      commonStyles.cpaGold;
                  }
                }}
                aria-label="Send help request"
              >
                {isSubmitting ? "Sending..." : "Send request"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Help;
